import { useParams, Link } from "react-router-dom";
import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import DOMPurify from "dompurify";
import { ArrowLeft, Phone } from "lucide-react";
import Layout from "@/components/Layout";
import Seo from "@/components/Seo";
import { getBlog, resolveImg } from "@/lib/api";
import { useSite } from "@/context/SiteContext";
import { waLink, generalWaMessage } from "@/lib/whatsapp";

// Minimal markdown -> html for our seeded content (##, -, **bold**, paragraphs)
const renderContent = (md = "") => {
  const esc = (s) => s.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
  const inline = (s) => esc(s).replace(/\*\*(.+?)\*\*/g, "<strong>$1</strong>").replace(/\*(.+?)\*/g, "<em>$1</em>");
  const lines = md.split("\n");
  let html = "";
  let inList = false;
  const closeList = () => { if (inList) { html += "</ul>"; inList = false; } };
  for (const raw of lines) {
    const line = raw.trim();
    if (!line) { closeList(); continue; }
    if (line.startsWith("## ")) { closeList(); html += `<h2>${inline(line.slice(3))}</h2>`; }
    else if (line.startsWith("- ")) { if (!inList) { html += "<ul>"; inList = true; } html += `<li>${inline(line.slice(2))}</li>`; }
    else { closeList(); html += `<p>${inline(line)}</p>`; }
  }
  closeList();
  return html;
};

const BlogDetail = () => {
  const { slug } = useParams();
  const site = useSite();
  const { data: b, isLoading, isError } = useQuery({ queryKey: ["blog", slug], queryFn: () => getBlog(slug) });
  const articleJsonLd = useMemo(() => (b ? {
    "@context": "https://schema.org",
    "@type": "Article",
    headline: b.title,
    image: b.cover_image,
    author: { "@type": "Organization", name: b.author },
    datePublished: b.created_at,
  } : undefined), [b]);

  if (isLoading) return <Layout><div className="mx-auto max-w-7xl px-6 py-24 text-muted-foreground">Loading…</div></Layout>;
  if (isError || !b) return <Layout><div className="mx-auto max-w-7xl px-6 py-24"><p>Article not found.</p><Link to="/blog" className="text-primary">Back to blog</Link></div></Layout>;

  return (
    <Layout>
      <Seo
        title={b.meta_title || b.title}
        description={b.meta_description || b.excerpt}
        jsonLd={articleJsonLd}
      />
      <article className="mx-auto max-w-3xl px-4 sm:px-8 py-12">
        <Link to="/blog" className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-primary mb-6"><ArrowLeft className="h-4 w-4" /> All Articles</Link>
        <span className="text-xs tracking-[0.15em] uppercase font-semibold text-primary">{b.category}</span>
        <h1 className="mt-2 font-heading text-4xl sm:text-5xl text-secondary tracking-tight leading-tight">{b.title}</h1>
        <p className="mt-3 text-muted-foreground">By {b.author}</p>
        <img src={resolveImg(b.cover_image)} alt={b.title} className="mt-8 rounded-xl w-full object-cover aspect-[16/9]" />
        <div className="prose-temple mt-8" dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(renderContent(b.content)) }} />

        <div className="mt-12 bg-cream border border-gold/40 rounded-xl p-7 text-center">
          <h3 className="font-heading text-2xl text-secondary">Need help choosing the right pooja?</h3>
          <p className="mt-2 text-muted-foreground">Message our priest team for honest guidance.</p>
          <div className="mt-5 flex flex-wrap justify-center gap-3">
            <a href={waLink(site.whatsapp_number, generalWaMessage)} target="_blank" rel="nofollow noopener noreferrer" data-testid="blog-whatsapp-button" className="inline-flex items-center gap-2 rounded-md bg-whatsapp px-6 py-3 font-semibold text-white hover:opacity-90"><Phone className="h-4 w-4" /> WhatsApp Us</a>
            <Link to="/poojas" className="inline-flex items-center gap-2 rounded-md bg-primary px-6 py-3 font-semibold text-white hover:bg-[#CC5204]" data-testid="blog-view-poojas">Browse Poojas</Link>
          </div>
        </div>
      </article>
    </Layout>
  );
};

export default BlogDetail;
